import React from "react";
import { Link } from "react-router-dom";
import ListingThumbnail from "./ListingThumbnail";
import { CATEGORY_LABEL, realTagline, displayHost } from "../../../lib/marketplace";
import DomainStatusBadge from "./DomainStatusBadge";

/**
 * The big banner at the top of the hub — Steam's featured capsule.
 *
 * `isPaid` distinguishes a genuinely boosted placement from the fallback. The fallback
 * is load-bearing, not defensive: /api/marketplace/shop returns featured: [] in prod
 * today (verified 2026-08-10), so WITHOUT it the redesign ships with a hole where its
 * largest element should be. We only claim "Featured" when someone actually paid.
 */
export default function SpotlightHero({ listing, isPaid }) {
  const tagline = realTagline(listing) || listing.description || "";
  const host = displayHost(listing.appUrl);

  return (
    <section className="group relative overflow-hidden border border-white/10 bg-[#0d0d10] transition-colors hover:border-lime-500/40">
      <div className="grid md:grid-cols-[1.35fr_1fr]">
        <Link
          to={`/limelite/marketplace/${listing.slug}`}
          className="relative block focus:outline-none focus-visible:ring-2 focus-visible:ring-lime-400"
          aria-label={`${listing.name} — view details`}
        >
          <ListingThumbnail listing={listing} size="lg" />
        </Link>

        <div className="flex flex-col justify-center gap-4 p-6 md:p-8">
          <span className="inline-flex w-fit items-center gap-2 border border-amber-400/40 bg-amber-400/10 px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.18em] text-amber-300">
            {isPaid ? "★ Featured" : "Most viewed"}
          </span>

          <div>
            <h2 className="text-2xl font-bold tracking-tight text-white md:text-3xl">{listing.name}</h2>
            {host && <p className="mt-1 font-mono text-xs text-silver-dark">{host}</p>}
          </div>

          {tagline && <p className="line-clamp-3 text-sm leading-relaxed text-silver-medium">{tagline}</p>}

          <div className="flex flex-wrap items-center gap-2">
            <span className="border border-white/15 bg-white/5 px-2 py-0.5 text-[11px] text-silver-medium">
              {CATEGORY_LABEL[listing.category] || listing.category}
            </span>
            {(listing.tags || []).slice(0, 4).map((t) => (
              <span key={t} className="border border-white/8 px-2 py-0.5 text-[11px] text-silver-dark">
                {t}
              </span>
            ))}
            <DomainStatusBadge listing={listing} />
          </div>

          <div className="mt-1 flex flex-wrap gap-3">
            {listing.appUrl && (
              <a
                href={listing.appUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="bg-lime-500 px-6 py-2.5 text-sm font-bold text-black transition-colors hover:bg-lime-400"
              >
                Open app →
              </a>
            )}
            <Link
              to={`/limelite/marketplace/${listing.slug}`}
              className="border border-white/15 px-6 py-2.5 text-sm font-medium text-silver-light transition-colors hover:border-white/35"
            >
              Details
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}
